import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@webapp/common/components/button';
import { CssPageBlock } from '@webapp/common/components/survey-custom';
import { ColorScheme, mapQuestionBlockPositionToAlignItems } from '@webapp/common/lib/ui';

import { Confirm } from '@webapp/survey/src/components/view/nav/confirm/confirm';
import {
    selectCurrentBundleIndex,
    selectInfo,
    selectIsStartPage,
    selectNextLoading,
    selectSendLoading,
    selectShowConfirmSms,
    selectShowNextButton,
    selectShowNextButtonByBundleIdx,
    selectShowPrevButton,
    selectShowPrevButtonByBundleIndex,
    selectShowSendButton,
    selectShowSendButtonByBundleIndex,
    selectWithClickReplyWithTwist,
} from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';
import { useStore } from '@webapp/survey/src/store';

import css from './nav.css';

const StartButton: FC<{
    darkText: boolean;
    loading: boolean;
    onClick: any;
    buttons: any;
}> = ({ buttons, darkText, loading, onClick }) => (
    <div className={css.action}>
        <Button data-testid='start-button' fluid color='blue' darkText={darkText} loading={loading} onClick={onClick}>
            {buttons.replyButtonLabel || 'Начать опрос'}
        </Button>
    </div>
);

const BackButton: FC<{
    darkText: boolean;
    onClick: any;
    buttons: any;
}> = ({ buttons, darkText, onClick }) => (
    <div className={cn(css.action, css.backButtonWrap)}>
        <Button fluid color='blue' darkText={darkText} onClick={onClick} className={css.backButton}>
            {buttons.backButtonLabel}
        </Button>
    </div>
);

const NextButton: FC<{
    darkText: boolean;
    loading: boolean;
    onClick: () => any;
    buttons: any;
}> = ({ buttons, darkText, loading, onClick }) => (
    <div className={css.action}>
        <Button
            data-testid='next-button'
            fluid
            color='blue'
            darkText={darkText}
            loading={loading}
            onClick={onClick}
            className={css.nextButton}
        >
            {buttons.nextButtonLabel}
        </Button>
    </div>
);

const SendButton: FC<{
    darkText: boolean;
    loading: boolean;
    onClick: () => any;
    buttons: any;
}> = ({ buttons, darkText, loading, onClick }) => (
    <div className={css.action}>
        <Button
            data-testid='send-button'
            fluid
            color='blue'
            darkText={darkText}
            loading={loading}
            onClick={onClick}
            className={css.nextButton}
        >
            {buttons.sendButtonLabel}
        </Button>
    </div>
);

export const SurveyNavForBundle: VFC<{ bundleIndex: number; debounceTimeout?: number }> = ({
    bundleIndex,
    debounceTimeout,
}) => {
    const showConfirmSms = useStateSelector(selectShowConfirmSms) === 'shown';
    const withClickReply = useStateSelector(selectWithClickReplyWithTwist);
    const { send } = useStore();
    const {
        design: {
            buttons,
            fonts: { buttonTextColor },
            view: { buttonBlockPosition },
        },
        authHash,
        preservePhone,
        params: {
            main: { recipientPhoneAuthType },
        },
    } = useStateSelector(selectInfo);
    const nextLoading = useStateSelector(selectNextLoading);
    const sendLoading = useStateSelector(selectSendLoading);
    const isStartPage = useStateSelector(selectIsStartPage);
    const isCurrentBundle = useStateSelector((ctx) => selectCurrentBundleIndex(ctx) === bundleIndex);
    const showNextButtonRaw = useStateSelector((ctx, state) =>
        selectShowNextButtonByBundleIdx(ctx, state, bundleIndex),
    );
    const showPrevButton = useStateSelector((ctx, state) => selectShowPrevButtonByBundleIndex(ctx, state, bundleIndex));
    const showSendButtonRaw = useStateSelector((ctx, state) =>
        selectShowSendButtonByBundleIndex(ctx, state, bundleIndex),
    );
    // Кнопки «Далее» и «Отправить» взаимоисключающие (последняя ли текущая
    // страница). Переключаем их без «дырки»: пока показывать новую нельзя,
    // на экране остаётся предыдущая кнопка (#1183).
    //
    // На текущей странице меняем сразу: «Отправить» <-> «Далее» должно
    // переключаться мгновенно, как только пересчиталась логика.
    // Задержка (debounceTimeout) нужна только соседним страницам: пока идёт
    // слайд, у уезжающей страницы «Далее» показывать нельзя — иначе она мигает.
    const [visibleAction, setVisibleAction] = useState<'next' | 'send' | null>(
        showNextButtonRaw ? 'next' : showSendButtonRaw ? 'send' : null,
    );

    useEffect(() => {
        if (showNextButtonRaw) {
            if (isCurrentBundle) {
                setVisibleAction('next');
                return;
            }

            const timeoutId = window.setTimeout(() => {
                setVisibleAction('next');
            }, debounceTimeout);

            return () => window.clearTimeout(timeoutId);
        }

        setVisibleAction(showSendButtonRaw ? 'send' : null);
    }, [showNextButtonRaw, showSendButtonRaw, isCurrentBundle, debounceTimeout]);

    const darkText = buttonTextColor === ColorScheme.DARK;
    const style: CSSProperties = useMemo(
        () => ({
            alignItems: mapQuestionBlockPositionToAlignItems[buttonBlockPosition],
            justifyContent: mapQuestionBlockPositionToAlignItems[buttonBlockPosition],
            padding: '0 var(--survey-content-padding-left-right)',
        }),
        [buttonBlockPosition],
    );

    const onNext = useCallback(() => send({ type: 'SELECT_NEXT_BUNDLE', noValidate: false }), [send]);
    const onPrev = useCallback(() => send({ type: 'SELECT_PREV_BUNDLE' }), [send]);
    const startSurvey = useCallback(() => send({ type: 'START' }), [send]);
    const sendFinish = () => {
        send({ type: 'SELECT_NEXT_BUNDLE', noValidate: false });
    };

    return (
        <div className={cn(CssPageBlock.NAVIGATION, css.nav, withClickReply && css.navFlexGrow)} style={style}>
            {isStartPage && <StartButton buttons={buttons} darkText={darkText} loading={false} onClick={startSurvey} />}
            {showPrevButton && <BackButton buttons={buttons} darkText={darkText} onClick={onPrev} />}
            {visibleAction === 'next' && (
                <NextButton buttons={buttons} darkText={darkText} loading={nextLoading} onClick={onNext} />
            )}
            {visibleAction === 'send' && (
                <SendButton buttons={buttons} darkText={darkText} loading={sendLoading} onClick={sendFinish} />
            )}
            {showConfirmSms && (
                <Confirm
                    onConfirm={() => send({ type: 'PASS_CONFIRM' })}
                    authHash={authHash}
                    preservePhone={preservePhone}
                    recipientPhoneAuthType={recipientPhoneAuthType}
                />
            )}
        </div>
    );
};
SurveyNavForBundle.displayName = 'SurveyNavForBundle';

export const SurveyNav: VFC = () => {
    const showConfirmSms = useStateSelector(selectShowConfirmSms) === 'shown';
    const { send } = useStore();
    const {
        design: {
            buttons,
            fonts: { buttonTextColor },
            view: { buttonBlockPosition },
        },
        authHash,
        preservePhone,
        params: {
            main: { recipientPhoneAuthType },
        },
    } = useStateSelector(selectInfo);
    const nextLoading = useStateSelector(selectNextLoading);
    const sendLoading = useStateSelector(selectSendLoading);
    const isStartPage = useStateSelector(selectIsStartPage);
    const showNextButton = useStateSelector(selectShowNextButton);
    const showPrevButton = useStateSelector(selectShowPrevButton);
    const showSendButton = useStateSelector(selectShowSendButton);

    const darkText = buttonTextColor === ColorScheme.DARK;
    const style: CSSProperties = useMemo(() => {
        if (isStartPage) {
            return {
                flexGrow: 1,
                alignItems: 'end',
                justifyContent: mapQuestionBlockPositionToAlignItems[buttonBlockPosition],
                marginBottom: '30px',
            };
        }

        return {
            alignItems: mapQuestionBlockPositionToAlignItems[buttonBlockPosition],
            justifyContent: mapQuestionBlockPositionToAlignItems[buttonBlockPosition],
        };
    }, [isStartPage, buttonBlockPosition]);

    const onNext = useCallback(() => send({ type: 'SELECT_NEXT_BUNDLE', noValidate: false }), [send]);
    const onPrev = useCallback(() => send({ type: 'SELECT_PREV_BUNDLE' }), [send]);
    const startSurvey = useCallback(() => send({ type: 'START' }), [send]);

    const sendFinish = () => {
        send({ type: 'SELECT_NEXT_BUNDLE', noValidate: false });
    };

    return (
        <div className={cn(CssPageBlock.NAVIGATION, css.nav)} style={style}>
            {isStartPage && <StartButton buttons={buttons} darkText={darkText} loading={false} onClick={startSurvey} />}
            {showPrevButton && <BackButton buttons={buttons} darkText={darkText} onClick={onPrev} />}
            {showNextButton && (
                <NextButton buttons={buttons} darkText={darkText} loading={nextLoading} onClick={onNext} />
            )}
            {showSendButton && (
                <SendButton buttons={buttons} darkText={darkText} loading={sendLoading} onClick={sendFinish} />
            )}
            {showConfirmSms && (
                <Confirm
                    onConfirm={() => send({ type: 'PASS_CONFIRM' })}
                    authHash={authHash}
                    preservePhone={preservePhone}
                    recipientPhoneAuthType={recipientPhoneAuthType}
                />
            )}
        </div>
    );
};
SurveyNav.displayName = 'SurveyNav';
